/*
 * Promo slider engine — draggable / swipeable track with autoplay + seamless loop.
 * Drives all promo banners (main, split, side) via data-attributes so the markup
 * stays prefix-agnostic.
 *
 * Contract:
 *   [data-ps-section]                 → root element (shares one index across its sliders)
 *   [data-ps-section][data-ps-href]   → optional whole-banner click target (URL)
 *   [data-ps-slider]                  → a slider viewport (overflow hidden)
 *     [data-ps-autoplay="true|false"]
 *     [data-ps-interval="2000"]       → ms
 *   [data-ps-track]                   → the moving flex row (direct child of slider)
 *   [data-ps-slide]                   → each slide (direct child of track)
 *   [data-ps-dots] > [data-ps-dot]    → optional indicators
 */
(function () {
  'use strict';

  if (window.__promoSliderReady) return;
  window.__promoSliderReady = true;

  var DURATION = 500; // keep in sync with CSS transition
  var EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

  function initSection(section) {
    if (section.dataset.psInit === '1') return;
    section.dataset.psInit = '1';

    var sliderEls = Array.prototype.slice.call(section.querySelectorAll('[data-ps-slider]'));
    if (!sliderEls.length) return;

    var units = [];
    var n = 0;
    var autoplay = false;
    var interval = 2000;

    sliderEls.forEach(function (slider) {
      var track = slider.querySelector('[data-ps-track]');
      if (!track) return;
      var slides = Array.prototype.slice.call(track.querySelectorAll('[data-ps-slide]'));
      if (!slides.length) return;

      n = slides.length;
      if (slider.getAttribute('data-ps-autoplay') === 'true') autoplay = true;
      var it = parseInt(slider.getAttribute('data-ps-interval'), 10);
      if (it) interval = it;

      // Clone first + last for seamless infinite looping.
      if (slides.length > 1) {
        var firstClone = slides[0].cloneNode(true);
        var lastClone = slides[slides.length - 1].cloneNode(true);
        firstClone.setAttribute('data-ps-clone', '');
        lastClone.setAttribute('data-ps-clone', '');
        track.appendChild(firstClone);
        track.insertBefore(lastClone, slides[0]);
      }

      var dots = Array.prototype.slice.call(slider.querySelectorAll('[data-ps-dot]'));
      units.push({ slider: slider, track: track, dots: dots });
    });

    if (!units.length) return;

    var hasLoop = n > 1;
    var pos = hasLoop ? 1 : 0; // track position (real slide 0 lives at index 1 with clones)
    var dragDelta = 0;
    var timer = null;
    var wrapTimer = null;

    function applyTransform(animate) {
      units.forEach(function (u) {
        u.track.style.transition = animate ? ('transform ' + DURATION + 'ms ' + EASE) : 'none';
        u.track.style.transform = 'translateX(calc(' + (-pos * 100) + '% + ' + dragDelta + 'px))';
      });
    }

    function realIndex() {
      if (!hasLoop) return 0;
      return (((pos - 1) % n) + n) % n;
    }

    function updateDots() {
      var ri = realIndex();
      units.forEach(function (u) {
        u.dots.forEach(function (d, i) {
          d.classList.toggle('is-active', i === ri);
        });
      });
    }

    function go(targetPos) {
      pos = targetPos;
      dragDelta = 0;
      applyTransform(true);
      updateDots();
      if (wrapTimer) { window.clearTimeout(wrapTimer); wrapTimer = null; }
      if (hasLoop && (pos === n + 1 || pos === 0)) {
        wrapTimer = window.setTimeout(function () {
          pos = pos === n + 1 ? 1 : n;
          applyTransform(false);
        }, DURATION + 30);
      }
    }

    function next() { if (hasLoop) go(pos + 1); }

    function start() {
      if (!autoplay || !hasLoop) return;
      stop();
      timer = window.setInterval(next, interval);
    }

    function stop() { if (timer) { window.clearInterval(timer); timer = null; } }

    // Initial paint
    applyTransform(false);
    updateDots();
    start();

    // Dots
    units.forEach(function (u) {
      u.dots.forEach(function (dot, i) {
        dot.addEventListener('click', function (e) {
          e.stopPropagation();
          if (hasLoop) go(i + 1);
          start();
        });
      });
    });

    if (hasLoop) {
      units.forEach(function (u) {

        /* ── Mobile: finger swipe (touch only — no mouse press-drag on desktop) ── */
        var startX = 0, startY = 0, downX = 0;
        var dragging = false, decided = false, horizontal = false, width = 1, pointerId = null;

        function onDown(e) {
          if (e.pointerType !== 'touch') return; // desktop uses scroll/swipe, not press-drag
          dragging = true; decided = false; horizontal = false;
          startX = e.clientX; startY = e.clientY; downX = e.clientX;
          width = u.slider.clientWidth || 1;
          pointerId = e.pointerId;
          stop();
        }

        function onMove(e) {
          if (!dragging) return;
          var dx = e.clientX - startX;
          var dy = e.clientY - startY;

          if (!decided) {
            if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
            decided = true;
            horizontal = Math.abs(dx) > Math.abs(dy);
            if (horizontal) {
              try { u.track.setPointerCapture(pointerId); } catch (err) {}
              u.track.classList.add('is-grabbing');
            } else {
              // Vertical intent → let the page scroll, abandon the drag
              dragging = false;
              start();
              return;
            }
          }

          if (horizontal) {
            if (e.cancelable) e.preventDefault();
            dragDelta = dx;
            applyTransform(false);
          }
        }

        function onUp(e) {
          if (!dragging) return;
          dragging = false;
          u.track.classList.remove('is-grabbing');

          var dx = e.clientX - downX;
          if (Math.abs(dx) > 6) {
            section.dataset.psDragged = '1';
            window.setTimeout(function () { delete section.dataset.psDragged; }, 0);
          }

          var threshold = Math.max(40, width * 0.15);
          if (dx <= -threshold) go(pos + 1);
          else if (dx >= threshold) go(pos - 1);
          else { dragDelta = 0; applyTransform(true); } // snap back
          start();
        }

        u.track.addEventListener('pointerdown', onDown);
        u.track.addEventListener('pointermove', onMove);
        u.track.addEventListener('pointerup', onUp);
        u.track.addEventListener('pointercancel', onUp);
        u.track.addEventListener('dragstart', function (e) { e.preventDefault(); });

        /* ── Desktop: two-finger touchpad swipe / horizontal scroll (one slide per swipe) ── */
        var wheelAccum = 0, wheelCooldown = false, wheelIdle = null;

        function onWheel(e) {
          // Only react to horizontal intent; leave vertical page scrolling alone
          if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
          e.preventDefault();
          stop();

          if (!wheelCooldown) {
            wheelAccum += e.deltaX;
            if (Math.abs(wheelAccum) >= 40) {
              if (wheelAccum > 0) go(pos + 1); else go(pos - 1);
              wheelAccum = 0;
              wheelCooldown = true; // one slide per gesture; ignore momentum
              window.setTimeout(function () { wheelCooldown = false; }, DURATION + 150);
            }
          }

          // Reset accumulation and resume autoplay once the gesture settles
          if (wheelIdle) window.clearTimeout(wheelIdle);
          wheelIdle = window.setTimeout(function () { wheelAccum = 0; start(); }, 600);
        }

        u.slider.addEventListener('wheel', onWheel, { passive: false });
      });
    }

    // Whole-banner click → button URL (suppressed right after a drag)
    var href = section.getAttribute('data-ps-href');
    if (href) {
      section.addEventListener('click', function (e) {
        if (section.dataset.psDragged) return;
        if (e.target.closest('[data-ps-dot]')) return;
        if (e.target.closest('a')) return;
        window.location.href = href;
      });
    }
  }

  function initAll(root) {
    var scope = root || document;
    Array.prototype.slice
      .call(scope.querySelectorAll('[data-ps-section]'))
      .forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor: (re)initialise a section when it is reloaded
  document.addEventListener('shopify:section:load', function (e) {
    var el = e.target;
    if (!el) return;
    if (el.matches && el.matches('[data-ps-section]')) { initSection(el); return; }
    var sec = el.querySelector && el.querySelector('[data-ps-section]');
    if (sec) initSection(sec);
  });
})();
